import { getCommonTableColumnsDefs, withDisplayName } from "@components/smart/GeneralFieldDefinition";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ICellValueObject } from "@components/table";
import { getDocumentTypeCodeFromEntityType } from "@odata/EntityTypes";
import {
    DocumentBusinessPartnerEntity,
    DocumentEntity,
    EntitySetName,
    EntityTypeName,
    PayingDocumentEntity
} from "@odata/GeneratedEntityTypes";
import { DocumentBusinessPartnerTypeCode } from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { IFormatOptions, transformToODataString } from "@odata/OData.utils";
import { getDocumentAttachmentsColumnDef, getDocumentTypeName } from "@pages/documents/Document.utils";
import { payslipPairDocumentAmountFormatter } from "@pages/payroll/payslip/Payslip.utils";
import i18next from "i18next";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { Sort, TextAlign, ValueType } from "../../../enums";
import { TValue } from "../../../global.types";
import BindingContext, { createPath } from "../../../odata/BindingContext";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { getDocumentStatusColumnDefinition } from "../../documents/DocumentCommonDefs";
import { getDocumentStatusFilterDefinition } from "../../documents/DocumentDef";
import { IDefinition, IGetDefinition } from "../../PageUtils";
import { DocumentStatusLocalPath, StatusEntities } from "../../reports/CommonDefs";

export const AmountPaidPath = BindingContext.localContext("AmountPaid");

// const handleClickAmount = async (e: React.MouseEvent, storage: Model, item: TRecordAny) => {
//     e.preventDefault();
//
//     const row = (storage as TSmartODataTableStorage).tableAPI.getRowsArray()?.find(row => row.dataId == item.Id);
//
//     storage.setCustomData({
//         amountDialogRow: row
//     });
//
//     storage.refresh();
// };

// export const docOnlyAmountPaidFormatter = (val: TValue, args: IFormatOptions): ICellValueObject => {
//     const storage = args.storage as TSmartODataTableStorage<IBankCustomData>;
//     const pairedDocuments = storage.getCustomData().pairedDocuments;
//     const id = args.item.Id;
//     const pairedLink = pairedDocuments?.[id];
//     const amount = pairedLink?.Amount ?? 0;
//
//     const formattedAmount = formatCurrency(amount, getCompanyCurrency(args.context));
//
//     const value = <b><ColoredText color={null}> {formattedAmount} </ColoredText></b>;
//     return amountDialogLinkFormatter(value, storage, args.item);
// };
//
// export const amountDialogLinkFormatter = (value: React.ReactNode, storage: TSmartODataTableStorage<IBankCustomData>, item: TRecordAny): ICellValueObject => {
//     const pairedDocuments = storage.getCustomData().pairedDocuments;
//     const id = item.Id;
//     const pairedLink = pairedDocuments?.[id];
//     const isPaired = !!pairedLink;
//     const isPairActionActive = isPairingActive(storage as TableStorage);
//
//     const isReadOnly = isPairingRowReadOnly(pairedLink, storage, storage.getCustomData().rootStorage);
//     const Icon = !isReadOnly ? EditSimpleIcon: null;
//
//     return {
//         tooltip: "",
//         value: (
//                 <LinkStyledBasic
//                         isPaired={isPaired}
//                         onClick={(e: React.MouseEvent) => {
//                             if (isPaired) {
//                                 handleClickAmount(e, storage, item);
//                             }
//                         }}>
//                     {isPaired && isPairActionActive && Icon &&
//                             <Icon width={IconSize.XS}/>
//                     }
//                     <LinkText>
//                         {value}
//                     </LinkText>
//                 </LinkStyledBasic>
//         )
//     };
// };

export const amountPaidFormatter = (val: TValue, args: IFormatOptions): ICellValueObject => {
    // const value = bankAmountPaidFormatter(val, args)?.value;
    // return amountDialogLinkFormatter(value, args.storage as TSmartODataTableStorage<IBankCustomData>, args.item);
    // todo: common formatter for amountPaid
    return {
        value: val,
        tooltip: val as string
    };
};

export const getDefinitionsFactory = (entitySet: EntitySetName.OtherLiabilities | EntitySetName.OtherReceivables): IGetDefinition => {

    const isOutgoingPayment = entitySet === EntitySetName.OtherLiabilities;
    const entityType = isOutgoingPayment ? EntityTypeName.OtherLiability : EntityTypeName.OtherReceivable;
    const documentType = getDocumentTypeCodeFromEntityType(entityType);

    const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {

        const filterBarDef: IFilterGroupDef[] = [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            isValueHelp: true,
            defaultFilters: [
                "NumberOurs",
                "NumberTheirs",
                "TransactionAmount",
                "TransactionCurrency",
                "BusinessPartner",
                "ClearedStatus",
                "DateDue",
                // "TransactionAmountToReceive",
                // "TransactionAmountToPay",
                "SymbolVariable",
                DocumentStatusLocalPath
            ],
            filterDefinition: {
                ...getDocumentStatusFilterDefinition(StatusEntities),
                NumberOurs: {},
                ...(isOutgoingPayment ? { NumberTheirs: {} } : {}),
                TransactionAmount: {},
                ...withDisplayName("BusinessPartner", {
                    label: i18next.t("Payslip:Table.BusinessPartner")
                }),
                ...withDisplayName("TransactionCurrency", "Name"),
                "BusinessPartner/Street": {},
                "BusinessPartner/City": {},
                "BusinessPartner/PostalCode": {},
                "BusinessPartner/Country": {},
                "BusinessPartner/FirstName": {},
                "BusinessPartner/LastName": {},
                "BusinessPartner/Email": {},
                "BusinessPartner/PhoneNumber": {},
                // TransactionAmountToPay: {},
                // TransactionAmountToReceive: {},
                DateDue: {},
                SymbolVariable: {}
            }
        }];

        const title = getDocumentTypeName(entityType, { usePlural: true });

        const table: ISplitPageTableDef = {
            filterBarDef,
            preventStoreVariant: true,
            id: "PayslipDocumentPairingSpecialTable",
            additionalProperties: [{ id: "TransactionCurrency" }, { id: "Currency" }, /*{ id: "TransactionAmountToReceive" }, { id: "TransactionAmountToPay" },*/
                { id: "ExchangeRatePerUnit" }, { id: "Currency" }, { id: "Amount" }, { id: "TransactionAmount" }, { id: "PostedStatus" }, { id: "DocumentTypeCode" },
                { id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.DocumentBusinessPartnerTypeCode) },
                { id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.FirstName) },
                { id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.LastName) },
                { id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.Employee) }],
            columnDefinition: {
                NumberOurs: {},
                NumberTheirs: {},
                PaymentMethodCode: {},
                SymbolVariable: {},
                "BankAccount/AccountNumber": {},
                "BankAccount/BankCode": {},
                TransactionAmountDue: {
                    // formatter: bankAmountDueFormatter
                },
                Note: { id: "Note" },
                ...getDocumentStatusColumnDefinition(),
                BusinessPartner: {
                    label: i18next.t("Payslip:Table.BusinessPartner"),
                    id: "BusinessPartner",
                    fieldSettings: {
                        displayName: "Name"
                    }
                },
                ...getCommonTableColumnsDefs(),
                [DocumentEntity.Attachments]: getDocumentAttachmentsColumnDef(documentType),
                [PayingDocumentEntity.DateIssued]: {},
                [PayingDocumentEntity.DateReceived]: {},
                [PayingDocumentEntity.DateDue]: {},
                [PayingDocumentEntity.TransactionAmount]: {},
                [AmountPaidPath]: {
                    label: i18next.t("Payslip:Pair.AmountPaid"),
                    textAlign: TextAlign.Right,
                    fieldSettings: {
                        disableSort: true,
                    },
                    formatter: payslipPairDocumentAmountFormatter
                }
            },
            filter: `${createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.DocumentBusinessPartnerTypeCode)} eq ${transformToODataString(DocumentBusinessPartnerTypeCode.Employee, ValueType.String)}`,
            columns: [
                DocumentEntity.NumberOurs,
                ...(isOutgoingPayment ? [DocumentEntity.NumberTheirs] : []),
                DocumentStatusLocalPath,
                DocumentEntity.BusinessPartner,
                AmountPaidPath,
                DocumentEntity.TransactionAmountDue,
                ...(isOutgoingPayment ? [PayingDocumentEntity.DateReceived] : [PayingDocumentEntity.DateIssued]),
                PayingDocumentEntity.DateDue,
                PayingDocumentEntity.DateCreated,
                PayingDocumentEntity.TransactionAmount,
                DocumentEntity.Attachments,
                DocumentEntity.Note
            ],
            initialSortBy: [{ id: DocumentEntity.DateCreated, sort: Sort.Desc }],
            title
        };

        return {
            entitySet,
            table
        };
    };

    getDefinitions.translationFiles = [
        getEnumNameSpaceName(EntityTypeName.PrEmploymentType)
    ];

    return getDefinitions;
};
